import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";
import { getData, postData, patchData, deleteData } from "../api/ApiServices";
import "../styles/AddItemModal.new.css";
import { ApiResponse, CustomerPart, CustomerResponse } from "../models/customerparts";
import AddCustomerForm from "../forms/AddCustomerPartForm";
import Pagination from "../view/common/pagination/Pagination";
import TableComponent from "./TableComponent";
import '../styles/common/sub-menu.css';
import '../styles/common/spinner.css';
import '../styles/common/icon-style.css'
import "../styles/ItemModels.css";
import '../styles/common/toplayout.css'
import '../styles/common/btn.css'
import '../styles/common/custom-alert.css';
import CustomAlert from "../view/common/alert/CustomAlert";

// step : 1
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";


const NewCustomer: React.FC = () => {
  const [customerParts, setCustomerParts] = useState<CustomerPart[]>([]);
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerPart | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Alert state
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(() => {});
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(() => {});
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);


  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const  permission = useSelector((state:RootState) => state.permissions)
  const [initializeCustomerParts, isCustomerPartsInitialized] = useState(false);

  useEffect(()=>{
    dispatch(resetFilters());
    isCustomerPartsInitialized(true);
  },[dispatch])

  useEffect(() => {
    if(initializeCustomerParts){
    fetchContributors();
    fetchCustomerParts();
  }}, [initializeCustomerParts,searchTerm, currentPage, itemsPerPage, filters]);


  const closeAlert = () => {
    setShowAlert(false);
  };

  // Configure and show alert
  const configureAlert = (
    message: string, 
    confirmAction: () => void, 
    cancelAction: () => void = () => {}, 
    confirmText: string = "OK", 
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const fetchContributors = async () => {
    setIsLoading(true);
    try {
      const response = await getData<ApiResponse<CustomerResponse>>("/contributors", { page: 1, limit: 100 });
      if (response?.data?.data && Array.isArray(response.data.data)) {
        const customerList = response.data.data.filter(contributor => contributor.type === "CUSTOMER" || contributor.type === "CUSTOMER&SUPPLIER");
        setCustomers(customerList);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error("Error fetching contributors:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomerParts = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData<ApiResponse<CustomerResponse>>("/customerparts", { page: currentPage, limit: itemsPerPage, search: searchTerm, filter: filters, });
      if (response?.data?.data && Array.isArray(response.data.data)) {
        setCustomerParts(response.data.data);
        setTotalItems(response.data.pagination.total);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setCustomerParts([]);
      }
    } catch (error) {
      console.error("Error fetching customer parts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Modify your handleSaveCustomer function in the NewCustomer component
const handleSaveCustomer = async (part: CustomerPart) => {
  setIsLoading(true);
  try {
    if (editingCustomer) {
      // Check if any changes were made
      const hasChanges = Object.keys(part).some(key => {
        // Skip comparing the _id field
        if (key === '_id') return false;
        // @ts-ignore - handle dynamic property access
        return part[key] !== editingCustomer[key];
      });

      if (!hasChanges) {
        // No changes were made
        configureAlert(
          "No changes were made.",
          () => {
            closeAlert();
            setIsAdding(false);
            setEditingCustomer(null);
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000 // Auto-close after 3 seconds
        );
        setIsLoading(false);
        return;
      }

      // Proceed with update if changes were made
      await patchData("/customerparts", editingCustomer._id || "", part);
      configureAlert(
        `Customer part has been updated successfully.`,
        () => {
          fetchCustomerParts();
          closeAlert();
        },
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    } else {
      // For new customer parts, continue as before
      await postData("/customerparts", part);
      configureAlert(
        `Customer part has been added successfully.`,
        () => {
          fetchCustomerParts();
          closeAlert();
        },
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    }
    setIsAdding(false);
  } catch (error) {
    console.error("Error saving customer part:", error);
    configureAlert(
      `Failed to ${editingCustomer ? "update" : "add"} customer part.`,
      closeAlert,
      closeAlert,
      "OK",
      "",
      true,
      false,
      3000
    );
  } finally {
    setIsLoading(false);
  }
};

  const handleDeleteCustomer = async (id: string) => {
    const customerToDelete = customerParts.find(c => c._id === id);
    
    if (customerToDelete) {
      // Show confirmation popup for deletion - no auto-close
      configureAlert(
        `Are you sure you want to Inactive this customer part?`,
        async () => {
          closeAlert(); // Close the confirmation dialog first
          setIsLoading(true);
          
          try {
            await deleteData("/customerparts", id);
            // After successful deletion, show success message with auto-close
            configureAlert(
              `Customer part has been Inactive successfully.`,
              () => {
                fetchCustomerParts();
                closeAlert(); // Explicitly close the alert when OK is clicked
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 // Auto-close after 3 seconds
            );
          } catch (error) {
            console.error("Error Inactive customer part:", error);
            configureAlert(
              "Failed to Inactive customer part.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000 // Auto-close after 3 seconds
            );
          } finally {
            setIsLoading(false);
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null // No auto-close for confirmation
      );
    }
  };

  const handleEditClick = (customer: CustomerPart) => {
    setEditingCustomer(customer);
    setIsAdding(true);
  };

  const handleClosePopup = () => {
    if (isAdding) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsAdding(false);
          setEditingCustomer(null);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null // No auto-close for confirmation
      );
    } else {
      setIsAdding(false);
      setEditingCustomer(null);
    }
  };

  const columns = [
    { key: "partNo", label: "Part No" },
    { key: "partName", label: "Part Name" },
    { key: "unitPrice", label: "Unit Price" },
    { key: "gst", label: "GST (%)" },
    { key: "sgst", label: "SGST (%)" },
    { key: "cgst", label: "CGST (%)" },
    { key: "igst", label: "IGST (%)" },
    { key: "posCode", label: "POS Code" },
    { key: "status", label: "Status" },
  ];

  return (
    <div className="item-models-container">
      {/* Header Section */}
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Master Item</span>
              <span>Customers</span>
            </div>
            {permission.menus.customerparts.create ?
            <div className="items-button-container tooltip-wrapper" onClick={() => setIsAdding(true)}>
              <button className="item-models-add-btn">
                <Plus size={16} />
              </button>
            </div>
            :<></>}
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="box-container">
        {isLoading ? (
          <div className="spinner"></div>
        ) : (
          <TableComponent
            columns={columns}
            data={customerParts}
            onEdit={handleEditClick}
            onDelete={handleDeleteCustomer}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) })); // Ensure it's a string
              });
            }}
          />
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>

      {/* Popup Form */}
      {isAdding && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-header">
              <h2>{editingCustomer ? "Edit Customer" : "Add New Customer"}</h2>
              <button className="close-button" onClick={handleClosePopup}>
                <X size={20} />
              </button>
            </div>
            <div className="popup-content">
              <AddCustomerForm
                onSave={handleSaveCustomer}
                onCancel={handleClosePopup}
                customers={customers}
                isProcessing={isLoading}
                isEditing={!!editingCustomer}
                editingCustomerPart={editingCustomer || undefined}
              />
            </div>
          </div>
        </div>
      )}

      {/* CustomAlert Component */}
      {showAlert && (
        <CustomAlert 
          message={alertMessage} 
          onConfirm={alertConfirmAction} 
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default NewCustomer;